import { message } from "antd";
import Axios, {
    type AxiosError,
    type AxiosRequestConfig,
    type AxiosRequestTransformer,
    type AxiosResponse,
} from "axios";
import { format } from "date-fns";
import qs from "query-string";

import { type BaseResponse } from "types/apiResponse/apiResponse";

import { convertServerDateStringToDate } from "utilities/api.utilities";
import {
    APIError,
    BadRequestError,
    ConflictError,
    defaultErrorMessage,
    LoginRedirectError,
    NoAccessError,
    NotFoundError,
    ServiceUnavailableError,
    TooManyRequestsError,
    UpgradeRequiredError,
} from "utilities/apiHandler";
import { getPortalTypeForHeader, handleRedirectToLogin } from "utilities/portal/portal";
import { checkForNewAppVersion } from "utilities/Versioning/versionWatcher";

// Returns undefined (never a fallback PortalType) when unresolved, so the header is omitted
// rather than sent with a wrong-but-valid value — see getPortalTypeForHeader for why that
// distinction matters.
const getPortalTypeWithFallback = () => {
    try {
        return getPortalTypeForHeader()?.toString();
    } catch {
        return undefined;
    }
};

/**
 * This is the default transformResponse function used for APIx endpoints to handle parsing dates correctly.
 * This can be chained with other response transforms.
 * @example
 * ```ts
 * useBuildersGetV2({
 *    request: {
 *        transformResponse: [defaultTransformResponse, myCustomTransformResponse],
 *    },
 * });
 * @param data The data from the server or from previous response transforms
 * @returns object parsed from JSON string or the original data if not a string
 */
export function defaultTransformResponse(data: unknown) {
    if (data && typeof data === "string") {
        return JSON.parse(data, responseJsonParseReviver);
    }
    return data;
}

/**
 * This is the default transformRequest function used for APIx endpoints to handle serializing dates correctly.
 * This can be chained with other request transforms.
 * @example
 * ```ts
 * useBuildersGetV2({
 *     request: {
 *         transformRequest: [myCustomTransformRequest, defaultTransformRequest],
 *     },
 * });
 *
 * ```
 * @param data The data being sent to the server or from previous request transforms
 * @returns JSON string of object with dates formatted as expected by the API
 */
export const defaultTransformRequest: AxiosRequestTransformer = (data: unknown) => {
    if (data instanceof FormData) {
        return data;
    }
    return JSON.stringify(data, requestJsonStringifyReplacer);
};

const axiosInstance = Axios.create({
    // Apply default config to all apix requests
    paramsSerializer: function (params) {
        const newParams = prepareParamsForSerialization(params) as Record<string, unknown>;
        return qs.stringify(newParams, { arrayFormat: "none" });
    },
    transformRequest: defaultTransformRequest,
    transformResponse: defaultTransformResponse,
});

axiosInstance.interceptors.response.use(undefined, async (error) => {
    if (error.response) {
        const contentType = error.response.headers["content-type"];

        // Check if the response data is a Blob and possibly JSON
        if (error.response.data instanceof Blob && contentType?.includes("application/json")) {
            try {
                // Read and parse the Blob as JSON
                const text = await error.response.data.text();
                error.response.data = JSON.parse(text);
            } catch (parseError) {
                console.warn("Failed to parse JSON from Blob error response:", parseError);
            }
        }

        switch (error.response.status) {
            case 400:
                throw new BadRequestError(error.response.data);
            case 401:
                handleRedirectToLogin();
                break;
            case 402:
                throw new UpgradeRequiredError(error.response.data);
            case 403:
                throw new NoAccessError(error.response.data);
            case 404:
                throw new NotFoundError(error.response.data);
            case 409:
                throw new ConflictError(error.response.data);
            case 429:
                throw new TooManyRequestsError(error.response.data, error.response.data?.message);
            case 503: {
                const data = error.response.data as BaseResponse & { retryAfterSeconds?: number };
                throw new ServiceUnavailableError(data, data.message, data.retryAfterSeconds);
            }
            default:
                throw new APIError(error.response);
        }
    } else {
        throw error;
    }
});

// react version handling for front-end reloading
axiosInstance.interceptors.response.use((orgResponse) => {
    if (orgResponse?.headers) {
        let reqAppVersion = orgResponse.headers["version"];

        if (reqAppVersion && typeof reqAppVersion === "string") {
            reqAppVersion = reqAppVersion.split(",")[0].trim();
        }

        if (reqAppVersion) {
            checkForNewAppVersion(reqAppVersion);
        }
    }

    return orgResponse;
});

/**
 * DO NOT import this method manually, it is used by Orval to override the default axios instance.
 * Instead, import the generated code from "@btnet/services-btnet/handlers/<domain>/<file>"
 * if you want to use apix.
 */
export const apixHandler = <T>(
    config: AxiosRequestConfig,
    options?: Omit<AxiosRequestConfig, "cancelToken">,
): Promise<AxiosResponse<T, any>> => {
    const source = Axios.CancelToken.source();
    const portalType = getPortalTypeWithFallback();
    const headers = {
        // Omitted (rather than defaulted to PortalType.ALL) when unresolved — see
        // getPortalTypeForHeader for why a wrong-but-valid header is worse than no header.
        ...(portalType !== undefined ? { PortalType: portalType } : {}),
        ...config.headers,
        ...options?.headers,
    };
    const promise = axiosInstance({
        ...config,
        ...options,
        headers,
        cancelToken: source.token,
    });

    // @ts-ignore
    promise.cancel = () => {
        source.cancel("Query was cancelled");
    };

    return promise;
};

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;
function isIsoDateString(value: any): value is string {
    return value && typeof value === "string" && isoDateFormat.test(value);
}

function responseJsonParseReviver(_key: string, value: unknown) {
    if (isIsoDateString(value)) {
        return convertServerDateStringToDate(value);
    }
    return value;
}

/**
 * Replacer function used for JSON.stringify to handle serialization of requests.
 * @param key
 * @param data
 * @returns
 */
function requestJsonStringifyReplacer(_key: string, data: { [key: string]: unknown }) {
    if (data === null || data === undefined || typeof data !== "object") {
        return data;
    }

    for (const [key, value] of Object.entries(data)) {
        if (value instanceof Date) {
            // Override the toJSON method to format the date as expected by the API
            data[key] = format(value, "yyyy-MM-dd'T'HH:mm:ss");
        }
    }

    return data;
}

/**
 * This method recursively searches through the body object and converts any date Objects to the expected api format. This
 * format does not contain the offset to preserve the date & time as seen by the user, which is expected to be in the builder's timezone.
 * @param body The data being read from the request
 */
function prepareParamsForSerialization(body: Record<string, any>) {
    if (body === null || body === undefined || typeof body !== "object") return body;

    if (Array.isArray(body)) {
        const result = [...body];
        for (let i = 0; i < result.length; i++) {
            const value = result[i];
            if (value instanceof Date) {
                result[i] = format(value, "yyyy-MM-dd'T'HH:mm:ss");
            } else if (value && typeof value === "object") {
                result[i] = prepareParamsForSerialization(value);
            }
        }
        return result;
    } else {
        const result = { ...body };
        for (const [key, value] of Object.entries(result)) {
            if (value instanceof Date) {
                result[key] = format(value, "yyyy-MM-dd'T'HH:mm:ss");
            } else if (value && typeof value === "object") {
                result[key] = prepareParamsForSerialization(value);
            }
        }
        return result;
    }
}

/**
 * Displays the error message from the server, if the server does not return an error message the default will be used
 * @param customMessage by default the message returned from the api will be shown, if the api failed but has no message "An error has occurred. Please try again." is shown
 */
export function showApixErrorMessage(e: Error, customMessage?: string, customDuration?: number) {
    if (e instanceof LoginRedirectError) {
        return;
    }

    const messageDuration = customDuration ?? 5;

    if (customMessage) {
        void message.error(customMessage, messageDuration);
        return;
    }

    const messageToDisplay: string = e.message || defaultErrorMessage;

    void message.error(messageToDisplay, messageDuration);
}
